/**
 * Form Builder V2 Wrapper
 *
 * Integrates the new FormBuilder V2 with the existing ReviewFormManager.
 * Handles data loading, saving, and conversion between formats.
 * Now includes template system support.
 */

import { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormBuilder } from '@/components/form-builder';
import { FormConfig, FormQuestion } from '@/components/form-builder/hooks/useFormBuilder';
import { useFormData, useSaveFormData } from '@/hooks/forms/useFormData';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  FormTemplateType,
  QuizConfig,
  NotificationConfig,
  ReviewSettings,
  defaultReviewSettings,
  formTemplates,
  getTemplateById,
} from '@/lib/forms/templates';

const BASE_URL = 'https://taginn.app';

interface FormBuilderV2WrapperProps {
  businessId: string | null;
  existingFormId?: string;
  onComplete: () => void;
  onCancel: () => void;
}

export function FormBuilderV2Wrapper({
  businessId,
  existingFormId,
  onComplete,
  onCancel,
}: FormBuilderV2WrapperProps) {
  const [initialForm, setInitialForm] = useState<FormConfig | undefined>();
  const [initialQuestions, setInitialQuestions] = useState<FormQuestion[]>([]);
  const [loading, setLoading] = useState(!!existingFormId);
  const [formId, setFormId] = useState<string | undefined>(existingFormId);
  // Track if this is a new form that hasn't been saved to DB yet
  const [isNewForm, setIsNewForm] = useState(!existingFormId);

  const { saveForm, saving } = useSaveFormData();

  // Load existing form data if editing
  useEffect(() => {
    const loadExistingForm = async () => {
      if (!existingFormId) {
        setLoading(false);
        return;
      }

      try {
        const { data: formData, error } = await supabase
          .from('review_forms')
          .select(`
            *,
            questions (*),
            businesses!inner (
              review_redirect_star_threshold,
              google_place_id
            )
          `)
          .eq('id', existingFormId)
          .single();

        if (error) throw error;

        // Extract business data for review settings
        const businessData = (formData as any).businesses as {
          review_redirect_star_threshold: number | null;
          google_place_id: string | null;
        } | null;

        // Convert to FormConfig format
        const form: FormConfig = {
          id: formData.id,
          title: formData.title,
          businessId: formData.business_id,
          builderVersion: formData.builder_version ?? 2,
          activeStatus: formData.active_status,
          enableStep2: formData.enable_step_2 ?? true,
          enableStep2High: formData.enable_step_2_high ?? true,
          enableStep2Low: formData.enable_step_2_low ?? true,
          mobileConfig: (formData.mobile_config as FormConfig['mobileConfig']) ?? {
            questionOrder: [],
            enabledQuestions: [],
          },
          desktopConfig: (formData.desktop_config as FormConfig['desktopConfig']) ?? {
            questionOrder: [],
            enabledQuestions: [],
          },
          animationSettings: (formData.animation_settings as FormConfig['animationSettings']) ?? {
            enabled: true,
            type: 'slide',
            springStiffness: 300,
            springDamping: 30,
          },
          keyboardNavEnabled: formData.keyboard_nav_enabled ?? true,
          progressBarConfig: (formData.progress_bar_config as FormConfig['progressBarConfig']) ?? {
            enabled: true,
            style: 'bar',
            showPercentage: false,
          },
          stepIndicatorConfig: (formData.step_indicator_config as FormConfig['stepIndicatorConfig']) ?? {
            enabled: true,
            showArrows: true,
          },
          uiTextConfig: formData.ui_text_config as FormConfig['uiTextConfig'],
          // Template-specific fields
          templateType: (formData.template_type as FormTemplateType) ?? 'review',
          quizConfig: formData.quiz_config as QuizConfig | undefined,
          notificationConfig: formData.notification_config as NotificationConfig | undefined,
          // Review settings - map from database columns
          reviewSettings: {
            starThreshold: businessData?.review_redirect_star_threshold ?? 4,
            googlePlaceId: businessData?.google_place_id ?? null,
            googleReviewUrl: businessData?.google_place_id
              ? `https://search.google.com/local/writereview?placeid=${businessData.google_place_id}`
              : '',
            highRatingEmailEnabled: (formData as any).high_rating_email_enabled ?? false,
            highRatingEmailRequired: (formData as any).high_rating_email_required ?? false,
            highRatingRewardType: (formData as any).high_rating_reward_type ?? 'none',
            highRatingCouponOfferId: (formData as any).high_rating_coupon_offer_id ?? null,
            highRatingLoyaltyProgramId: (formData as any).high_rating_loyalty_program_id ?? null,
            lowRatingEmailEnabled: (formData as any).low_rating_email_enabled ?? false,
            lowRatingEmailRequired: (formData as any).low_rating_email_required ?? false,
            lowRatingRewardType: (formData as any).low_rating_reward_type ?? 'none',
            lowRatingCouponOfferId: (formData as any).low_rating_coupon_offer_id ?? null,
            lowRatingLoyaltyProgramId: (formData as any).low_rating_loyalty_program_id ?? null,
          } as ReviewSettings,
        };

        // Convert questions
        const questions: FormQuestion[] = (formData.questions || [])
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
          .map((q: any) => ({
            id: q.id,
            formId: q.form_id,
            type: q.question_type_v2 || mapLegacyType(q.type),
            text: q.text,
            required: q.required ?? false,
            placeholder: q.placeholder || undefined,
            options: q.options?.map((opt: any, idx: number) => ({
              id: opt.id || String(idx),
              label: opt.value,
              value: opt.value.toLowerCase().replace(/\s+/g, '_'),
            })),
            validationRules: q.validation_rules || {},
            uiConfig: q.ui_config || { showLetterIndicators: true },
            deviceConfig: q.device_config || {
              mobile: { order: q.order ?? 0, visible: true },
              desktop: { order: q.order ?? 0, visible: true },
            },
            visibleOnHighRating: q.visible_on_high_rating ?? true,
            visibleOnLowRating: q.visible_on_low_rating ?? true,
            branchingRules: convertBranchingRules(q.branching_rules),
            createdAt: q.created_at,
            // Quiz-specific fields
            correctAnswer: q.correct_answer,
            points: q.points ?? 1,
            explanation: q.explanation,
          }));

        // Populate question order if empty
        if (form.mobileConfig.questionOrder.length === 0) {
          const ids = questions.map((q) => q.id);
          form.mobileConfig.questionOrder = ids;
          form.mobileConfig.enabledQuestions = ids;
          form.desktopConfig.questionOrder = ids;
          form.desktopConfig.enabledQuestions = ids;
        }

        setInitialForm(form);
        setInitialQuestions(questions);
        setFormId(existingFormId);
      } catch (error) {
        console.error('Error loading form:', error);
        toast.error('Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    loadExistingForm();
  }, [existingFormId]);

  // For new forms, just mark as ready to show template selector
  useEffect(() => {
    if (existingFormId || !businessId) return;
    setIsNewForm(true);
    setLoading(false);
  }, [businessId, existingFormId]);

  // Handle creating a new form from template (immediately saves to database)
  const handleCreateFromTemplate = useCallback(
    async (templateType: FormTemplateType): Promise<{ formId: string } | null> => {
      if (!businessId) return null;

      try {
        const template = getTemplateById(templateType);
        const templateName = template?.name || 'Review Form';

        // Create the form in the database
        const { data: newForm, error: insertError } = await supabase
          .from('review_forms')
          .insert({
            business_id: businessId,
            title: `New ${templateName}`,
            active_status: false,
            enable_step_2: template?.defaultConfig.enableStep2 ?? true,
            enable_step_2_high: template?.defaultConfig.enableStep2High ?? true,
            enable_step_2_low: template?.defaultConfig.enableStep2Low ?? true,
            builder_version: 2,
            template_type: templateType,
            mobile_config: { questionOrder: [], enabledQuestions: [] },
            desktop_config: { questionOrder: [], enabledQuestions: [] },
            animation_settings: {
              enabled: true,
              type: 'slide',
              springStiffness: 300,
              springDamping: 30,
            },
            keyboard_nav_enabled: true,
            progress_bar_config: {
              enabled: true,
              style: 'bar',
              showPercentage: false,
            },
            quiz_config: template?.defaultConfig.quizConfig || null,
            notification_config: template?.defaultConfig.notificationConfig || null,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Update state with the real form ID
        setFormId(newForm.id);
        setIsNewForm(false);

        // Set initial form config for the builder
        const form: FormConfig = {
          id: newForm.id,
          title: newForm.title,
          businessId: businessId,
          builderVersion: 2,
          activeStatus: false,
          enableStep2: template?.defaultConfig.enableStep2 ?? true,
          enableStep2High: template?.defaultConfig.enableStep2High ?? true,
          enableStep2Low: template?.defaultConfig.enableStep2Low ?? true,
          templateType: templateType,
          mobileConfig: { questionOrder: [], enabledQuestions: [] },
          desktopConfig: { questionOrder: [], enabledQuestions: [] },
          animationSettings: {
            enabled: true,
            type: 'slide',
            springStiffness: 300,
            springDamping: 30,
          },
          keyboardNavEnabled: true,
          progressBarConfig: {
            enabled: true,
            style: 'bar',
            showPercentage: false,
          },
          quizConfig: template?.defaultConfig.quizConfig,
          notificationConfig: template?.defaultConfig.notificationConfig,
        };
        setInitialForm(form);

        toast.success('Form created');
        return { formId: newForm.id };
      } catch (error) {
        console.error('Error creating form:', error);
        toast.error('Failed to create form');
        return null;
      }
    },
    [businessId]
  );

  // Handle save
  const handleSave = useCallback(
    async (form: FormConfig, questions: FormQuestion[]) => {
      const result = await saveForm(form, questions, isNewForm);

      if (result.success) {
        // If this was a new form, update state with the real ID from the database
        if (isNewForm && result.newFormId) {
          setFormId(result.newFormId);
          setIsNewForm(false);
        }
        toast.success('Form saved successfully');
      } else {
        toast.error('Failed to save form');
      }
    },
    [saveForm, isNewForm]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Business ID is required</p>
        <Button variant="outline" onClick={onCancel} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <FormBuilder
      formId={formId}
      businessId={businessId}
      initialForm={initialForm}
      initialQuestions={initialQuestions}
      onSave={handleSave}
      onCreateFromTemplate={handleCreateFromTemplate}
      onBack={onCancel}
      previewUrl={formId ? `${BASE_URL}/review/${formId}` : undefined}
      showTemplateSelector={isNewForm && !formId}
      className="h-full"
    />
  );
}

// Helper: Map legacy question types
function mapLegacyType(type: string): string {
  const mapping: Record<string, string> = {
    text: 'short_text',
    textarea: 'long_text',
    multiple_choice: 'dropdown',
    checkboxes: 'checkboxes',
    rating: 'rating',
  };
  return mapping[type] || 'short_text';
}

// Helper: Convert branching rules
function convertBranchingRules(dbRules: unknown) {
  if (!dbRules) return undefined;

  const rules = dbRules as {
    answers?: {
      [key: string]: {
        action: string;
        targetQuestionId?: string;
      };
    };
  };

  if (!rules.answers) return undefined;

  return Object.entries(rules.answers).map(([answerValue, action]) => ({
    conditionType: 'equals' as const,
    conditionValue: answerValue,
    targetQuestionId:
      action.action === 'skip_to_end'
        ? 'end'
        : action.action === 'goto_question'
        ? action.targetQuestionId || 'end'
        : 'end',
  }));
}
